<template>
  <div class="pfm">
    <header class="pfm__head">
      <div class="pfm__eyebrow">
        <span class="pfm__dot"></span>WaizmannVergleich / ANTRAGSTELLER
      </div>
      <h2 class="pfm__title">1. Antragsteller (Versicherungsnehmer)</h2>
      <p class="pfm__sub">
        Bitte tragen Sie die persönlichen Angaben des Versicherungsnehmers ein.
      </p>
    </header>

    <v-form ref="antragform" @submit.prevent="submitPersonal" class="pfm__form">
      <section class="psec">
        <header class="psec__head">
          <span class="psec__num">01</span>
          <div>
            <div class="psec__eyebrow">SCHRITT</div>
            <h3 class="psec__title">Ihr Geburtstag</h3>
          </div>
        </header>
        <div class="psec__body">
          <div class="prow prow--birth">
            <label class="plabel">Geburtstag</label>
            <div class="pinput">
              <input-geburtstag-select
                @update:year="(year) => handleBirthdayChange('vn', 'jahra', year)"
                @update:month="(month) => handleBirthdayChange('vn', 'monata', month)"
                @update:day="(day) => handleBirthdayChange('vn', 'taga', day)"
                :year="antragdata.jahra"
                :month="antragdata.monata"
                :day="antragdata.taga"
                :componentKeyStatus="componentKeyStatus"
              />
              <p v-if="berechnung.ber_alter < 18" class="phint">
                Bitte Ihren <b class="text-green">eigenen</b> Geburtstag angeben. Den
                Geburtstag des <b>zu versichernden Kindes</b> (unter 18 Jahre) tragen
                Sie bitte weiter unten unter „Daten der versicherten Person" ein.
              </p>
            </div>
          </div>

          <div class="perr" v-if="fehlerGeburtstagBerechnungAntragsteller">
            <b>Achtung — das Alter stimmt nicht mit Ihrer Berechnung überein.</b>
            <span>
              Bitte wiederholen Sie die Berechnung mit dem richtigen Geburtsdatum
              oder geben Sie das korrekte Geburtsdatum ein.
            </span>
          </div>
          <div class="perr" v-if="fehlerGeburtstagAge">
            <b>Achtung — der Versicherungsnehmer muss mindestens 18 Jahre alt sein.</b>
          </div>
          <div class="perr" v-if="fehlerGeburtstag">
            <b>Achtung — bitte ändern Sie das Datum.</b>
            <span>
              Bitte geben Sie hier den Geburtstag des Versicherungsnehmers ein,
              nicht den der „versicherten Person".
            </span>
          </div>
        </div>
      </section>

      <section class="psec">
        <header class="psec__head">
          <span class="psec__num">02</span>
          <div>
            <div class="psec__eyebrow">SCHRITT</div>
            <h3 class="psec__title">Geschlecht &amp; Staatsangehörigkeit</h3>
          </div>
        </header>
        <div class="psec__body">
          <div class="prow prow--split">
            <div>
              <label class="plabel">Geschlecht</label>
              <v-radio-group v-model="antragdata.geschlecht" inline class="pradio">
                <v-radio :value="0" label="Frau" />
                <v-radio :value="1" label="Mann" />
              </v-radio-group>
            </div>
            <div>
              <label class="plabel">Staatsangehörigkeit</label>
              <v-text-field
                v-model="antragdata.staat"
                type="text"
                :rules="[checkTextFeld('staat')]"
              />
            </div>
          </div>
        </div>
      </section>

      <section class="psec">
        <header class="psec__head">
          <span class="psec__num">03</span>
          <div>
            <div class="psec__eyebrow">SCHRITT</div>
            <h3 class="psec__title">Name</h3>
          </div>
        </header>
        <div class="psec__body">
          <div class="prow prow--split">
            <div>
              <label class="plabel">Vorname</label>
              <v-text-field
                v-model="antragdata.antragstellerv"
                type="text"
                :rules="[checkTextFeld('antragstellerv')]"
                :append-inner-icon="getIconSuccessText('antragstellerv')"
              />
            </div>
            <div>
              <label class="plabel">Nachname</label>
              <v-text-field
                v-model="antragdata.antragsteller"
                type="text"
                :rules="[checkTextFeld('antragsteller')]"
                :append-inner-icon="getIconSuccessText('antragsteller')"
              />
            </div>
          </div>
        </div>
      </section>

      <section class="psec">
        <header class="psec__head">
          <span class="psec__num">04</span>
          <div>
            <div class="psec__eyebrow">SCHRITT</div>
            <h3 class="psec__title">Adresse</h3>
          </div>
        </header>
        <div class="psec__body">
          <div class="prow prow--street">
            <div>
              <label class="plabel">Straße</label>
              <v-text-field
                v-model="antragdata.strasse"
                type="text"
                :rules="[checkTextFeld('strasse')]"
                :append-inner-icon="getIconSuccessText('strasse')"
              />
            </div>
            <div>
              <label class="plabel">Hausnummer</label>
              <v-text-field
                v-model="antragdata.hausnummer"
                type="text"
                :rules="[checkTextFeld('hausnummer')]"
                :append-inner-icon="getIconSuccessText('hausnummer')"
              />
            </div>
          </div>

          <div class="prow prow--plz">
            <div>
              <label class="plabel">PLZ</label>
              <v-text-field
                v-model="antragdata.plz"
                type="text"
                :rules="[checkTextFeld('plz')]"
                :append-inner-icon="getIconSuccessText('plz')"
                validate-on-blur
              />
            </div>
            <div>
              <label class="plabel">Wohnort</label>
              <v-text-field
                v-model="antragdata.ort"
                type="text"
                :rules="[checkTextFeld('ort')]"
                :append-inner-icon="getIconSuccessText('ort')"
              />
            </div>
          </div>
        </div>
      </section>

      <section class="psec">
        <header class="psec__head">
          <span class="psec__num">05</span>
          <div>
            <div class="psec__eyebrow">SCHRITT</div>
            <h3 class="psec__title">E-Mail</h3>
          </div>
        </header>
        <div class="psec__body">
          <div class="prow">
            <label class="plabel">E-Mail</label>
            <div class="pinput">
              <v-text-field
                v-model="antragdata.email"
                type="text"
                :rules="[validateEmail(antragdata.email)]"
                validate-on-blur
                :append-inner-icon="getIconSuccess()"
              />
            </div>
          </div>
        </div>
      </section>

      <section class="psec">
        <header class="psec__head">
          <span class="psec__num">06</span>
          <div>
            <div class="psec__eyebrow">SCHRITT</div>
            <h3 class="psec__title">Handy</h3>
          </div>
        </header>
        <div class="psec__body">
          <div class="prow">
            <label class="plabel">Handy-Nummer</label>
            <div class="pinput">
              <v-text-field
                v-model="antragdata.tel_handy"
                type="text"
                :rules="[checkTextFeld('tel_handy')]"
                :append-inner-icon="getIconSuccessText('tel_handy')"
                validate-on-blur
              />
              <p class="phint">
                Wir rufen Sie nur im absoluten Notfall an z. B. bei wichtigen
                Rückfragen — niemals zu Werbezwecken.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section class="psec">
        <header class="psec__head">
          <span class="psec__num">07</span>
          <div>
            <div class="psec__eyebrow">SCHRITT</div>
            <h3 class="psec__title">Beruf</h3>
          </div>
        </header>
        <div class="psec__body">
          <div class="prow prow--split">
            <div>
              <label class="plabel">Status Beruf</label>
              <v-select
                label="Status Beruf"
                v-model="antragdata.beruf"
                :items="beruf"
                item-value="id"
                item-title="name"
                single-line
              />
            </div>
            <div>
              <label class="plabel">Berufsbezeichnung</label>
              <v-text-field
                v-model="antragdata.berufb"
                type="text"
                :rules="[checkTextFeld('berufb')]"
                :append-inner-icon="getIconSuccessText('berufb')"
              />
            </div>
          </div>
        </div>
      </section>

      <div class="pdiv" v-if="tarif.tarif_only_vn == 0">
        <header class="pfm__head pfm__head--inner">
          <div class="pfm__eyebrow">
            <span class="pfm__dot"></span>WaizmannVergleich / VERSICHERTE PERSON
          </div>
          <h2 class="pfm__title">1.2. Wen möchten Sie versichern?</h2>
        </header>
        <div class="psec__body">
          <v-radio-group v-model="antragdata.person" class="pradio">
            <v-radio :value="1" label="sich selbst" />
            <v-radio
              @click="changeVpDefault"
              :value="2"
              label="andere Person (z. B. Ihr Kind, Ihre Ehefrau …)"
            />
          </v-radio-group>
        </div>
      </div>

      <div
        v-if="antragdata.person == 2 && tarif.tarif_only_vn == 0"
        class="pdiv"
      >
        <header class="pfm__head pfm__head--inner">
          <div class="pfm__eyebrow">
            <span class="pfm__dot"></span>WaizmannVergleich / VERSICHERTE PERSON
          </div>
          <h2 class="pfm__title">Daten der versicherten Person</h2>
          <p class="pfm__sub">
            Diese Felder bitte nur ausfüllen, wenn der Antragsteller eine
            <strong>andere Person</strong> als der Versicherungsnehmer ist.
            <strong class="text-green">Kinder und Personen unter 18 Jahren bitte hier eintragen.</strong>
          </p>
        </header>

        <section class="psec">
          <header class="psec__head">
            <span class="psec__num">01</span>
            <div>
              <div class="psec__eyebrow">SCHRITT</div>
              <h3 class="psec__title">Geburtstag der versicherten Person</h3>
            </div>
          </header>
          <div class="psec__body">
            <div class="prow prow--birth">
              <label class="plabel">Geburtstag</label>
              <div class="pinput">
                <input-geburtstag-select
                  @update:year="(year) => handleBirthdayChange('VP', 'jahrv', year)"
                  @update:month="(month) => handleBirthdayChange('VP', 'monatv', month)"
                  @update:day="(day) => handleBirthdayChange('VO', 'tagv', day)"
                  :year="antragdata.jahrv"
                  :month="antragdata.monatv"
                  :day="antragdata.tagv"
                  :componentKeyStatus="componentKeyStatus"
                />
              </div>
            </div>
            <div class="perr" v-if="fehlerGeburtstagBerechnungVersicherungsnehmer">
              <b>Achtung — das Alter stimmt nicht mit Ihrer Berechnung überein.</b>
              <span>
                Bitte wiederholen Sie die Berechnung mit dem richtigen
                Geburtsdatum oder geben Sie das korrekte Geburtsdatum ein.
              </span>
            </div>
          </div>
        </section>

        <section class="psec">
          <header class="psec__head">
            <span class="psec__num">02</span>
            <div>
              <div class="psec__eyebrow">SCHRITT</div>
              <h3 class="psec__title">Geschlecht &amp; Staatsangehörigkeit</h3>
            </div>
          </header>
          <div class="psec__body">
            <div class="prow prow--split">
              <div>
                <label class="plabel">Geschlecht</label>
                <v-radio-group v-model="antragdata.geschlechtv" inline class="pradio">
                  <v-radio :value="0" label="Frau" />
                  <v-radio :value="1" label="Mann" />
                </v-radio-group>
              </div>
              <div>
                <label class="plabel">Staatsangehörigkeit</label>
                <v-text-field
                  v-model="antragdata.vstaat"
                  type="text"
                  :rules="[checkTextFeldVP('vstaat')]"
                />
              </div>
            </div>
          </div>
        </section>

        <section class="psec">
          <header class="psec__head">
            <span class="psec__num">03</span>
            <div>
              <div class="psec__eyebrow">SCHRITT</div>
              <h3 class="psec__title">Name</h3>
            </div>
          </header>
          <div class="psec__body">
            <div class="prow prow--split">
              <div>
                <label class="plabel">Vorname</label>
                <v-text-field
                  v-model="antragdata.vversicherungsnehmer"
                  type="text"
                  :rules="[checkTextFeldVP('vversicherungsnehmer')]"
                  :append-inner-icon="getIconSuccessTextVP('vversicherungsnehmer')"
                />
              </div>
              <div>
                <label class="plabel">Nachname</label>
                <v-text-field
                  v-model="antragdata.versicherungsnehmer"
                  type="text"
                  :rules="[checkTextFeldVP('versicherungsnehmer')]"
                  :append-inner-icon="getIconSuccessTextVP('versicherungsnehmer')"
                />
              </div>
            </div>
          </div>
        </section>

        <section class="psec">
          <header class="psec__head">
            <span class="psec__num">04</span>
            <div>
              <div class="psec__eyebrow">SCHRITT</div>
              <h3 class="psec__title">Beruf der versicherten Person</h3>
            </div>
          </header>
          <div class="psec__body">
            <div class="prow prow--split">
              <div>
                <label class="plabel">Status Beruf</label>
                <v-select
                  label="Status Beruf"
                  v-model="antragdata.vberuf"
                  :items="beruf"
                  item-value="id"
                  item-title="name"
                  single-line
                />
              </div>
              <div>
                <label class="plabel">Berufsbezeichnung</label>
                <v-text-field
                  v-model="antragdata.berufbv"
                  type="text"
                  :rules="[checkTextFeldVP('berufbv')]"
                  :append-inner-icon="getIconSuccessTextVP('berufbv')"
                />
                <p class="phint">
                  Bitte geben Sie die Berufsbezeichnung der zu versichernden Person
                  an, z. B. Schüler, Student, Hausfrau oder Bezeichnung des
                  ausgeübten Berufes. Bei Kindern geben Sie hier einfach „Kind" ein.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="pdiv">
        <header class="pfm__head pfm__head--inner">
          <div class="pfm__eyebrow">
            <span class="pfm__dot"></span>WaizmannVergleich / KRANKENKASSE
          </div>
          <h2 class="pfm__title">
            Angaben zur Krankenkasse
            <span class="text-green">(der versicherten Person)</span>
          </h2>
        </header>

        <section class="psec">
          <div class="psec__body">
            <div class="prow prow--kk">
              <div>
                <label class="plabel">Krankenkasse der versicherten Person</label>
                <v-text-field
                  v-model="antragdata.kvgesetzlich"
                  type="text"
                  :rules="[checkTextFeld('kvgesetzlich')]"
                  :append-inner-icon="getIconSuccessText('kvgesetzlich')"
                />
                <p class="phint">
                  Hinweis: BKK alleine ist KEINE Krankenkasse. Bitte vollständige
                  Bezeichnung angeben.
                </p>
              </div>
              <div>
                <label class="plabel">Seit wann?</label>
                <v-text-field
                  v-model="antragdata.versseit"
                  type="number"
                  label="Seit wann?"
                />
              </div>
            </div>

            <div class="prow prow--split">
              <div>
                <label class="plabel">Versicherungsstatus</label>
                <v-select
                  label="Versicherungsstatus"
                  v-model="antragdata.versstatus"
                  :items="versicherungsStati"
                  item-value="id"
                  item-title="name"
                  single-line
                />
              </div>
              <div>
                <label class="plabel">
                  Haben Sie eine ältere Zusatzversicherung
                  <strong>mit Zahnleistungen</strong>, die Sie langfristig
                  <strong>zusätzlich behalten</strong> wollen?
                </label>
                <v-radio-group v-model="antragdata.privat" inline class="pradio">
                  <v-radio :value="2" label="Nein" />
                  <v-radio :value="1" label="Ja" />
                </v-radio-group>
              </div>
            </div>

            <div
              v-if="antragdata.privat == 1 && tarif.tarif_int_doppelversicherung == 2"
              class="pwarn"
            >
              Bei der <strong>{{ tarif.un_name }}</strong> müssen Sie alle
              bestehenden Zusatzversicherungen,
              <strong>die auch für Zähne leisten</strong>,
              <strong class="text-red">kündigen</strong> (sog. Doppelversicherungsverbot).
            </div>
          </div>
        </section>

        <header class="pfm__head pfm__head--inner">
          <div class="pfm__eyebrow">
            <span class="pfm__dot"></span>WaizmannVergleich / KONTAKT
          </div>
          <h2 class="pfm__title">Bitte prüfen Sie nochmal kurz Ihre Kontaktdaten</h2>
        </header>

        <section class="psec">
          <div class="psec__body">
            <div class="prow prow--split">
              <div>
                <label class="plabel">Vorname</label>
                <v-text-field
                  v-model="antragdata.antragstellerv"
                  type="text"
                  :rules="[checkTextFeld('antragstellerv')]"
                  :append-inner-icon="getIconSuccessText('antragstellerv')"
                />
              </div>
              <div>
                <label class="plabel">Nachname</label>
                <v-text-field
                  v-model="antragdata.antragsteller"
                  type="text"
                  :rules="[checkTextFeld('antragsteller')]"
                  :append-inner-icon="getIconSuccessText('antragsteller')"
                />
              </div>
            </div>
            <div class="prow">
              <label class="plabel">E-Mail</label>
              <div class="pinput">
                <v-text-field
                  v-model="antragdata.email"
                  type="text"
                  :rules="[validateEmail(antragdata.email)]"
                  validate-on-blur
                  :append-inner-icon="getIconSuccess()"
                />
              </div>
            </div>
          </div>
        </section>

        <div v-if="isAntragError" class="pwarn pwarn--err">
          <h3 class="pwarn__title">
            Bitte verbessern Sie noch ein paar Kleinigkeiten
          </h3>
          <p>
            Bitte prüfen Sie noch Ihre Angaben in den
            <b class="text-red">rot markierten</b> Feldern.
          </p>
        </div>

        <div v-if="insertStarted" class="ploading">
          <v-progress-circular :size="40" color="primary" indeterminate />
          <h3 class="ploading__title">Bitte haben Sie noch ein wenig Geduld …</h3>
        </div>

        <section class="psubmit" v-if="!insertStarted">
          <v-btn
            @click="submitPersonal"
            color="primary"
            size="large"
            type="submit"
            class="psubmit__btn"
            append-icon="mdi-arrow-right-circle"
          >
            <span v-if="!sharedDataStore.isMobile()">
              jetzt kostenlos Infopaket anfordern
            </span>
            <span v-else>Jetzt anfordern</span>
          </v-btn>
          <div class="psubmit__trust">
            <v-icon size="14" class="text-green">mdi-shield-check-outline</v-icon>
            <span>
              Wir versenden das Infopaket vorab per E-Mail (i. d. R. wenige
              Stunden), zusätzlich erhalten Sie die Unterlagen per Post.
            </span>
          </div>
          <div class="psubmit__ssl" v-if="!sharedDataStore.isMobile()">
            <v-icon size="14" class="text-green">mdi-lock</v-icon>
            <span>Ihre Anforderung ist SSL-geschützt.</span>
          </div>
        </section>
      </div>
    </v-form>
  </div>
</template>

<script setup>
  import {
    ref,
    getCurrentInstance,
    defineProps,
    reactive,
    watchEffect,
    defineEmits,
    watch,
    onMounted,
  } from "vue";
  import InputGeburtstagSelect from "@/components/berechnung/form/InputFormGeburtstagSelect";
  import { useSharedDataStore } from "@/stores/sharedData";
  import { useLoaderStore } from "@/stores/loaderStore";
  import BaseCalls from "@/services/BaseCalls";
  const loaderStore = useLoaderStore();

  const sharedDataStore = useSharedDataStore();
  const { proxy } = getCurrentInstance();
  const emit = defineEmits(["updateAntrag"]);

  const props = defineProps({
    berechnung: {
      type: Object,
    },
    tarif: {
      type: Object,
    },
  });
  const componentKeyStatus = ref(0);

  function setLoading(loading) {
    loaderStore.setLoader(loading);
  }
  const versicherungsStati = reactive([
    { id: "2", name: "pflichtversichert" },
    { id: "1", name: "freiwillig versichert" },
    { id: "3", name: "familienversichert als Ehepartner" },
    { id: "4", name: "familienversichert als Kind" },
  ]);
  const beruf = reactive([
    { id: "1", name: "angestellt" },
    { id: "2", name: "selbständig" },
    { id: "7", name: "Schüler" },
    { id: "11", name: "Kind" },
    { id: "10", name: "Auszubildender" },
    { id: "3", name: "Student" },
    { id: "4", name: "Beamte/r" },
    { id: "5", name: "angestellt im öffentlichen Dienst" },
    { id: "6", name: "Hausmann/Hausfrau" },
    { id: "8", name: "Rentner" },
    { id: "9", name: "keine Erwerbstätigkeit" },
  ]);

  const fehlerGeburtstagBerechnungAntragsteller = ref(false);
  const fehlerGeburtstagBerechnungVersicherungsnehmer = ref(false);

  const antragform = ref();
  const fehlerGeburtstag = ref(false);
  const insertStarted = ref(false);
  const user = ref(null);
  const antragdata = ref({
    startInsert: false,
    vorvers_t: "Keine_Vorversicherung",
    wechsel_zzv: 2,
    person: 1,
    privat: 2,
    abschluss_email: 2,
    antragstellerv: "",
    antragsteller: "",
    strasse: "",
    hausnummer: "",
    plz: "",
    ort: "",
    staat: "deutsch",
    vstaat: "deutsch",
    email: "",
    arbeitgeber: "",
    varbeitgeber: "",
    tel_handy: "",
    beruf: "1",
    vberuf: "7",
    berufb: "",
    berufbv: "",
    geschlechtv: 1,
    geschlecht: 0,
    vversicherungsnehmer: "",
    versicherungsnehmer: "",
    versseit: "2020",
    versstatus: "2",
    vorvers: "",
    bemerkung: "",
    wechsel_detail: "",
    taga: 12,
    monata: 6,
    jahra: 1984,
    tagv: 12,
    monatv: 6,
    jahrv: 2014,
    kvgesetzlich: "",
  });

  const isAntragError = ref(false);
  const fehlerGeburtstagAge = ref(false);

  function checkVNFormular() {
    let fields = [
      "antragstellerv",
      "antragsteller",
      "strasse",
      "ort",
      "tel_handy",
      "staat",
      "berufb",
      "kvgesetzlich",
      "plz",
    ];

    if (!antragdata.value || typeof antragdata.value !== "object") {
      console.error("Fehler: antragdata.value ist nicht korrekt definiert.");
      return false;
    }

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      let validateFunction = checkTextFeld(field);
      let errorField = validateFunction(antragdata.value[field]);

      if (errorField !== true) {
        console.log("Fehler im Feld: " + field + " - " + errorField);
        return false;
      }
    }

    isAntragError.value = false;
    return true;
  }

  function checkVPFormular() {
    let fields = [
      "berufbv",
      "vstaat",
      "versicherungsnehmer",
      "vversicherungsnehmer",
    ];
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      let validateFunction = checkTextFeldVP(field);
      let fieldValue = antragdata.value[field];
      let errorField = validateFunction(fieldValue);

      if (errorField !== true) {
        console.log(`Fehler im Feld '${field}': ${errorField}`);
        return false;
      }
    }

    return true;
  }

  function checkVnAge() {
    fehlerGeburtstagAge.value = false;
    if (antragdata.value.age1 < 18) {
      fehlerGeburtstagAge.value = true;
      console.log("age1 < 18");
      return false;
    }
    return true;
  }

  function checkGeburtstagBerechnungFormular() {
    if (
      antragdata.value.person == 1 &&
      props.berechnung.ber_jahr != antragdata.value.jahra
    ) {
      fehlerGeburtstagBerechnungAntragsteller.value = true;
      return false;
    }

    if (
      antragdata.value.person == 2 &&
      props.berechnung.ber_jahr != antragdata.value.jahrv
    ) {
      fehlerGeburtstagBerechnungVersicherungsnehmer.value = true;
      return false;
    }
    return true;
  }

  async function submitPersonal() {
    if (!checkVnAge()) {
      isAntragError.value = true;
      console.log("error vn age");
      return false;
    }
    if (!checkGeburtstagBerechnungFormular()) {
      isAntragError.value = true;
      console.log("error berechnungen stimmen nicht überein");
      return false;
    }

    if (!checkVNFormular()) {
      isAntragError.value = true;
      console.log("error vn form");
      return false;
    }

    // Sicherung: person muss 1 (selbst) oder 2 (andere Person) sein, sonst wird
    // die VP-Pruefung uebersprungen und Kinder-Anfragen kommen ohne Namen an.
    if (antragdata.value.person != 1 && antragdata.value.person != 2) {
      isAntragError.value = true;
      console.log("error person nicht gewaehlt");
      return false;
    }

    if (antragdata.value.person == 2) {
      if (!checkVPFormular()) {
        isAntragError.value = true;
        console.log("error vp form");
        return false;
      }
    }

    if (!isAntragError.value) {
      if (!insertStarted.value) {
        insertStarted.value = true;
        setLoading(true);
        antragdata.value.startInsert = true;
        emit("updateAntrag", antragdata.value);
      }
    }
  }

  function changeVpDefault() {
    console.log("changeVpDefault: " + antragdata.value.person);
    antragdata.value.tagv = parseInt(props.berechnung.ber_tag);
    antragdata.value.monatv = parseInt(props.berechnung.ber_monat);
    antragdata.value.jahrv = parseInt(props.berechnung.ber_jahr);
    antragdata.value.geschlechtv = parseInt(props.berechnung.ber_geschlecht);
  }

  function setDefaultValue() {
    console.log("default: " + props.berechnung.ber_id);
    antragdata.value.age1 = props.berechnung.ber_alter;
    if (props.berechnung.ber_alter >= 18) {
      antragdata.value.taga = parseInt(props.berechnung.ber_tag);
      antragdata.value.monata = parseInt(props.berechnung.ber_monat);
      antragdata.value.jahra = parseInt(props.berechnung.ber_jahr);
      antragdata.value.geschlecht = parseInt(props.berechnung.ber_geschlecht);
    }
    if (props.berechnung.ber_alter < 18) {
      antragdata.value.taga = 15;
      antragdata.value.monata = 6;
      antragdata.value.jahra = parseInt(props.berechnung.ber_jahr) - 27;
      antragdata.value.geschlecht = parseInt(props.berechnung.ber_geschlecht);

      antragdata.value.tagv = parseInt(props.berechnung.ber_tag);
      antragdata.value.monatv = parseInt(props.berechnung.ber_monat);
      antragdata.value.jahrv = parseInt(props.berechnung.ber_jahr);
      antragdata.value.geschlechtv = parseInt(props.berechnung.ber_geschlecht);
      antragdata.value.person = 2;
    }
    componentKeyStatus.value += 1;
  }

  function getIconSuccess() {
    let check = validateEmail(antragdata.value.email);
    console.log("checkEmail" + check);
    if (check !== true) {
      return "mdi-alert-circle";
    }
    return "mdi-check-bold";
  }
  const getIconSuccessText = (feld) => {
    let validateFunction = checkTextFeld(feld);
    let errorField = validateFunction(antragdata.value[feld]);
    if (errorField !== true) {
      return "mdi-alert-circle";
    }
    return "mdi-check-bold";
  };

  const getIconSuccessTextVP = (feld) => {
    let validateFunction = checkTextFeldVP(feld);
    let errorField = validateFunction(antragdata.value[feld]);
    if (errorField !== true) {
      return "mdi-alert-circle ";
    }
    return "mdi-check-bold";
  };

  const handleBirthdayChange = (vers, type, value) => {
    fehlerGeburtstagBerechnungVersicherungsnehmer.value = false;
    fehlerGeburtstagBerechnungAntragsteller.value = false;

    console.log("change:  " + type + " value " + value);
    antragdata.value[type] = value;

    if (type.includes("VP")) {
      handleBirthdayChangedAntrVp();
    } else {
      handleBirthdayChangedAntr();
    }
  };

  const handleBirthdayChangedAntrVp = () => {
    fehlerGeburtstag.value = false;
    calcAge("vp");
  };

  const handleBirthdayChangedAntr = () => {
    fehlerGeburtstag.value = false;
    calcAge("antr");
  };

  const calcAge = (type) => {
    const day = type === "vp" ? antragdata.value.tagv : antragdata.value.taga;
    const month =
      type === "vp" ? antragdata.value.monatv : antragdata.value.monata;
    const year =
      type === "vp" ? antragdata.value.jahrv : antragdata.value.jahra;
    antragdata.value.age1 = proxy.$calcAge(day, month, year);
  };

  const checkTextFeldVP = (feld) => {
    return (value) => {
      if (antragdata.value.person === 2) {
        switch (feld) {
          case "vversicherungsnehmer":
            if (!value || value.trim() === "") {
              return "Bitte geben Sie den Vornamen der zu versichernden Person an.";
            }
            break;
          case "versicherungsnehmer":
            if (!value || value.trim() === "") {
              return "Bitte geben Sie den Nachnamen der zu versichernden Person an.";
            }
            break;
          case "vstaat":
            if (!value || value.trim() === "") {
              return "Bitte geben Sie die Staatsangehörigkeit an.";
            }
            break;
          case "berufbv":
            if (!value || value.trim() === "") {
              return "Bitte geben Sie den Beruf bzw. Tätigkeit an.";
            }
            break;
          default:
            return true;
        }
      }
      return true;
    };
  };


  const checkTextFeld = (feld) => {
    return (value) => {
      if (feld === "plz") {
        if (
          value.length !== 5 ||
          !/^\d+$/.test(value) ||
          value.trim() === "" ||
          !value
        ) {
          console.log(
            `Fehler in Feld '${feld}': Bitte geben Sie Ihre Postleitzahl 5-stellig an.`
          );
          return "Bitte geben Sie Ihre Postleitzahl 5-stellig an.";
        }

        return true;
      }

      if (!value || value.trim() === "") {
        console.log("check feld " + feld + "- " + value);
        let error = "";
        switch (feld) {
          case "antragstellerv":
            error = "Bitte geben Sie Ihren Vornamen an.";
            break;
          case "antragsteller":
            error = "Bitte geben Sie Ihren Nachnamen an.";
            break;
          case "strasse":
            error = "Bitte geben Sie Ihre Straße an.";
            break;
          case "hausnummer":
            error = "Bitte geben Sie Ihre Hausnummer an.";
            break;
          case "ort":
            error = "Bitte geben Sie Ihren Wohnort an.";
            break;
          case "tel_handy":
            error = "Bitte geben Sie Ihre Handy-Nummer an.";
            break;
          case "staat":
            error = "Bitte geben Sie Ihre Staatsangehörigkeit an.";
            break;
          case "berufb":
            error = "Bitte geben Sie Ihren Beruf an.";
            break;
          case "kvgesetzlich":
            error =
              "Bitte geben Sie die Krankenversicherung der versicherten Person an.";
            break;
          default:
            error = true;
            break;
        }
        if (error !== true) {
          console.log(`Fehler in Feld '${feld}': ${error}`);
          return error;
        }
        return true;
      }
      return true;
    };
  };

  async function getUser() {
    let guestSelect = {};
    guestSelect.action = "getUser";
    try {
      const resp = await BaseCalls.postGuest(guestSelect);
      if (resp.data && resp.data.user) {
        user.value = resp.data.user;

      }
    } catch (err) {
      console.log("error berechnung");
    }
  }
  const isEmailAllowed = () => {
    if (user.value && user.value.user_antrag_email_sperre !== '') {
      const sperreArray = user.value.user_antrag_email_sperre
        .split(';')
        .map(entry => entry.trim().toLowerCase());

      const userEmail = antragdata.value.email.toLowerCase();

      for (const sperreItem of sperreArray) {
        if (sperreItem && userEmail.includes(sperreItem)) {
          console.log("Gesperrter Begriff gefunden: " + sperreItem);
          return { isValid: false, error: 'Bitte geben Sie die Email Ihres Kunden an.' };
        }
      }
    }
    return { isValid: true };
  }

  const validateEmail = (email) => {
    const result = proxy.$validateEmail(email);
    const resultUser = isEmailAllowed();

    if (!resultUser.isValid){
        return resultUser.error;
    }

    if (result.isValid) {
        return true;
    } else {
        return result.error;
    }
  };
  watch(
    () => antragdata.value,
    () => {
      if (isAntragError.value) {
        isAntragError.value = false;
      }
    },
    { deep: true }
  );

  watch(
    () => antragdata.value.age1,
    (newAge) => {
      if (newAge < 18) {
        antragdata.value.person = 2;
      }
    }
  );
  watchEffect(() => {
    setDefaultValue();
  });
  onMounted(() => {
    getUser();
  });
</script>

<style scoped>
  .pfm {
    width: 100%;
    max-width: 100%;
    margin: 0;
    padding: 0;
    color: var(--wz-ink-2);
  }

  .pfm__head {
    border: 1px solid var(--wz-border);
    border-left: 3px solid var(--wz-blue);
    background: #fff;
    padding: 12px 16px;
    margin-bottom: 14px;
  }
  .pfm__head--inner {
    margin-top: 22px;
  }
  .pfm__eyebrow {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: var(--wz-ink-4);
    display: flex;
    align-items: center;
  }
  .pfm__dot {
    width: 6px;
    height: 6px;
    background: var(--wz-blue);
    display: inline-block;
    margin-right: 8px;
  }
  .pfm__title {
    font-size: clamp(18px, 2.2vw, 22px);
    font-weight: 700;
    letter-spacing: -0.2px;
    color: var(--wz-ink);
    line-height: 1.2;
    margin: 4px 0 2px;
  }
  .pfm__sub {
    font-size: 13px;
    color: var(--wz-ink-5);
    margin: 4px 0 0;
  }

  .pfm__form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .psec {
    background: #fff;
    border: 1px solid var(--wz-border);
    border-left: 3px solid var(--wz-blue);
    border-radius: 3px;
  }
  .psec--optional {
    border-left-color: var(--wz-orange);
  }
  .psec--err {
    border-left-color: var(--wz-red);
    background: #fff8f8;
  }
  .psec__head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px 8px;
    border-bottom: 1px solid var(--wz-border-3);
  }
  .psec__num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: var(--wz-surface-blue);
    color: var(--wz-blue);
    font-weight: 800;
    font-size: 13px;
    letter-spacing: 0.4px;
    border: 1px solid var(--wz-border-blue);
    flex-shrink: 0;
  }
  .psec__eyebrow {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: var(--wz-ink-4);
  }
  .psec__title {
    font-size: 15px;
    font-weight: 700;
    color: var(--wz-ink);
    margin: 2px 0 0;
    line-height: 1.25;
  }
  .psec__body {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .prow {
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 12px;
    align-items: start;
  }
  .prow--split {
    grid-template-columns: 1fr 1fr;
  }
  .prow--street {
    grid-template-columns: 2fr 1fr;
  }
  .prow--plz {
    grid-template-columns: 1fr 2fr;
  }
  .prow--kk {
    grid-template-columns: 2fr 1fr;
  }
  .prow--birth {
    grid-template-columns: 140px minmax(0, 1fr);
  }
  .prow--birth .pinput {
    min-width: 0;
    max-width: 360px;
  }
  .plabel {
    font-size: 12.5px;
    font-weight: 600;
    color: var(--wz-ink-3);
    line-height: 1.3;
    padding-top: 10px;
  }
  .pinput {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .phint {
    font-size: 12px;
    color: var(--wz-ink-5);
    margin: 6px 0 0;
    line-height: 1.4;
  }

  .perr {
    background: var(--wz-red-bg);
    border: 1px solid var(--wz-red-border);
    border-left: 3px solid var(--wz-red);
    color: var(--wz-red);
    padding: 8px 12px;
    font-size: 13px;
    line-height: 1.45;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .perr span {
    color: var(--wz-ink-2);
  }

  .pwarn {
    background: var(--wz-orange-bg);
    border: 1px solid var(--wz-rec-border);
    border-left: 3px solid var(--wz-orange);
    padding: 10px 12px;
    color: var(--wz-ink-2);
    font-size: 13px;
    line-height: 1.45;
  }
  .pwarn--err {
    background: var(--wz-red-bg);
    border-color: var(--wz-red-border);
    border-left-color: var(--wz-red);
  }
  .pwarn__title {
    font-size: 14px !important;
    font-weight: 700;
    color: var(--wz-ink) !important;
    margin: 0 0 4px;
  }

  .pradio {
    margin: 0;
    padding: 0;
  }

  .pdiv {
    margin-top: 18px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .ploading {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    background: var(--wz-surface-blue);
    border: 1px solid var(--wz-border-blue);
    border-left: 3px solid var(--wz-blue);
  }
  .ploading__title {
    margin: 0;
    color: var(--wz-blue) !important;
    font-size: 15px;
  }

  .psubmit {
    background: #fff;
    border: 1px solid var(--wz-border);
    border-left: 3px solid var(--wz-blue);
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
  }
  .psubmit__btn {
    align-self: flex-start;
    height: 52px !important;
    font-size: 16px !important;
    font-weight: 700;
    text-transform: none !important;
    letter-spacing: 0 !important;
  }
  .psubmit__trust,
  .psubmit__ssl {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    color: var(--wz-ink-5);
    line-height: 1.45;
  }

  :deep(.v-field) {
    border-radius: 3px !important;
  }
  :deep(.v-field__outline) {
    --v-field-border-opacity: 0.6;
  }
  :deep(.mdi-check-bold) {
    color: var(--wz-green) !important;
  }
  :deep(.mdi-alert-circle) {
    color: var(--wz-orange) !important;
  }

  @media (max-width: 780px) {
    .pfm {
      padding: 0;
    }
    .pfm__form {
      gap: 10px;
    }
    .psec__head {
      padding: 8px 12px 6px;
      gap: 10px;
    }
    .psec__num {
      width: 28px;
      height: 28px;
      font-size: 12px;
    }
    .psec__title {
      font-size: 14px;
    }
    .psec__body {
      padding: 12px;
      gap: 10px;
    }
    .prow,
    .prow--split,
    .prow--street,
    .prow--plz,
    .prow--kk {
      grid-template-columns: 1fr;
      gap: 6px;
    }
    .plabel {
      padding-top: 0;
    }
    .psubmit {
      padding: 12px;
    }
    .psubmit__btn {
      width: 100%;
      align-self: stretch;
      height: 48px !important;
      font-size: 15px !important;
    }
  }
</style>
